import { motion } from 'framer-motion';
import { useState } from 'react';

// Mock data matching your resumeData structure
const consulting = [
  {
    title: "AI Tools Integration Training - AccelTree Technologies",
    scope: "Training engineering teams on AI-enhanced development workflows",
    outcomes: [
      "Successfully trained team on GitHub Copilot integration",
      "Implemented Gemini Pro for code assistance",
      "Established prompt engineering best practices",
      "Enhanced developer productivity through AI tool adoption"
    ]
  },
  {
    title: "Startup AI Strategy Consulting",
    scope: "Technical consultation for startups across multiple domains",
    outcomes: [
      "Guided AI integration strategies for logistics companies",
      "Architected MVP solutions for e-commerce platforms",
      "Designed LLM-based solutions for edtech products",
      "Provided technical roadmap for startups"
    ]
  }
];

export default function Consulting() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section className="py-24 px-6 bg-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.06, 0.03]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-12 text-center"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-mono tracking-wider">
              CLIENT PARTNERSHIPS
            </span>
          </motion.div>

          <h2 className="text-5xl md:text-4xl font-black mb-6">
            <span className="bg-gradient-to-r from-gray-200 via-white to-gray-200 bg-clip-text text-transparent">
              Consulting Projects
            </span>
          </h2>

          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "100%" }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent max-w-md mx-auto"
          />
        </motion.div>

        {/* Projects Row */}
        <div className="flex flex-wrap gap-6 justify-center items-stretch">
          {consulting.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.2 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="group relative w-[420px] flex flex-col"
            >
              <div className="relative bg-gradient-to-br from-zinc-900/50 to-black/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 overflow-hidden transition-all duration-500 hover:border-green-500/30 flex flex-col h-full">

                {/* Hover gradient */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={hoveredIndex === index ? {
                    backgroundPosition: ['0% 0%', '100% 100%']
                  } : {}}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />

                {/* Top accent */}
                <motion.div
                  className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: false, amount: 0.5 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />

                <div className="relative p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start gap-3 mb-4">
                      <motion.div
                        className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <span className="text-green-400 text-lg font-bold">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </motion.div>

                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-green-400 transition-colors duration-300">
                          {project.title}
                        </h3>

                        <div className="mb-4 pb-4 border-b border-zinc-800/50">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                            <span className="text-xs font-bold text-green-400 uppercase tracking-wider">
                              Project Scope
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm leading-snug pl-3">
                            {project.scope}
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                            <span className="text-xs font-bold text-green-400 uppercase tracking-wider">
                              Key Outcomes
                            </span>
                          </div>
                          <div className="space-y-2 pl-3">
                            {project.outcomes.map((outcome, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -15 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: false, amount: 0.5 }}
                                transition={{ duration: 0.3, delay: i * 0.1 }}
                                className="flex items-start gap-2"
                              >
                                <motion.div
                                  whileHover={{ scale: 1.2 }}
                                  className="flex-shrink-0 w-1.5 h-1.5 bg-green-500/50 rounded-full mt-1"
                                />
                                <span className="text-gray-400 text-sm leading-snug">
                                  {outcome}
                                </span>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom corner accent */}
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
